import React, { useEffect, useMemo } from "react";
import { useMountEffect } from "primereact/hooks";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { Message } from "primereact/message";
import Section from "../../common/section";
import { NotificationConstants } from "../../../constants";
import {
  notificationsRequest,
  setDashboard,
  updateNotificationReadRequest,
} from "../../../store/slices/selfSlice";
import * as RoleHelper from "../../../helpers/RoleHelper";
import dayjs from "dayjs";
import { Tooltip } from "primereact/tooltip";

export const NotificationAlerts = ({
  notificationsRequest,
  notifications,
  error,
  action,
  requesting,
  updateNotificationReadRequest,
  user,
  isRoleSwitching,
  dashboard,
  setDashboard,
  resetNavigation,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();

  useMountEffect(() => {
    notificationsRequest();
  });

  useEffect(() => {
    if (!error && !requesting && action === "update-notification-read") {
      notificationsRequest();
    }
  }, [error, action, requesting]);

  const messages = useMemo(() => {
    return (
      (notifications.length > 0 &&
        notifications.map((notification, index) => {
          const notificationType = NotificationConstants.notificationTypeOf(
            notification.type
          );
          // A link to an admin page needs the admin dashboard and access to the page
          const canOpen =
            !notificationType.dashboard ||
            (isRoleSwitching &&
              RoleHelper.checkRoleScope(
                user?.role?.scope,
                notificationType.scope,
                notificationType.permission
              ));
          // A type whose text names its subject itself (certificate downloads)
          const statusLabel =
            notificationType.labelByStatus?.[notification.batchStatus];
          return {
            key: `notification-${index}`,
            severity: notificationType.severity,
            detail: intl.formatMessage(
              { id: statusLabel || notificationType.label },
              { name: notification.subjectName }
            ),
            summary: notificationType.labelByStatus
              ? null
              : notification.subjectName,
            closable: false,
            sticky: true,
            createdAt: notification.createdAt,
            id: notification.id,
            to: (canOpen && notificationType.to) || null,
            dashboard: notificationType.dashboard,
            linkLabel:
              notificationType.linkLabel &&
              intl.formatMessage({ id: notificationType.linkLabel }),
          };
        })) ||
      []
    );
  }, [notifications, intl, isRoleSwitching, user?.role?.scope]);

  // The notices are shown on the learner dashboard, where admin pages are not
  // mounted (the app would send the link back to the dashboard): switch to the
  // admin dashboard first, as the header's dashboard switch does.
  const openLink = (event, message) => {
    if (message.dashboard && message.dashboard !== dashboard) {
      event.preventDefault();
      resetNavigation();
      setDashboard(message.dashboard);
      navigate(message.to);
    }
  };

  const markAsRead = (message) => {
    updateNotificationReadRequest({ id: message.id });
  };

  return (
    <Section
      title="notification_alerts"
      className="edelxms-widget notitications"
      headerAction={
        (notifications.length && (
          <div data-testid="notifications-clear-all" onClick={() => updateNotificationReadRequest({ id: "all" })}>
            <Tooltip target=".pi.pi-trash" />
            <i
              className="pi pi-trash"
              style={{ fontSize: "1.5rem", cursor: "pointer" }}
              data-pr-tooltip={intl.formatMessage({ id: "mark_all_as_read" })}
              data-pr-position="left"
            />
          </div>
        )) ||
        null
      }
    >
      {(messages.length &&
        messages.map((message) => {
          return (
            <Message
              key={message.key}
              severity={message.severity}
              className={message.className}
              style={message.style}
              text={
                <div style={{ width: "100%" }}>
                  <div className="flex gap-2 align-items-center justify-content-between">
                    <div>
                      <div>
                        {[message.summary, message.detail]
                          .filter(Boolean)
                          .join(" ")}
                      </div>
                      {(message.to && (
                        <Link
                          to={message.to}
                          onClick={(event) => openLink(event, message)}
                        >
                          {message.linkLabel}
                        </Link>
                      )) ||
                        null}
                      <div style={{ marginTop: 4, fontSize: "12px" }}>
                        {dayjs(message.createdAt).format("DD MMM YYYY")}
                      </div>
                    </div>
                    <div data-testid="notification-dismiss" onClick={() => markAsRead(message)}>
                      <i
                        className="pi pi-times"
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  </div>
                </div>
              }
            ></Message>
          );
        })) || <span>{intl.formatMessage({ id: "no_notifications" })}</span>}
    </Section>
  );
};

const mapStateToProps = (state) => ({
  notifications: state.self.notifications,
  action: state.self.action,
  requesting: state.self.requesting,
  error: state.self.error,
  user: state.self.user,
  isRoleSwitching: state.self.isRoleSwitching,
  dashboard: state.self.dashboard,
});

const mapDispatchToProps = (dispatch) => ({
  notificationsRequest: () => dispatch(notificationsRequest()),
  updateNotificationReadRequest: (message) =>
    dispatch(updateNotificationReadRequest(message)),
  setDashboard: (data) => dispatch(setDashboard(data)),
  // By type: importing navigationSlice here would read the constants barrel
  // (MenuConstants) while this widget is loaded through it
  resetNavigation: () => dispatch({ type: "navigation/resetNavigation" }),
});

export default connect(mapStateToProps, mapDispatchToProps)(NotificationAlerts);
